import { useMemo, useState } from 'react'
import { geoNaturalEarth1, geoPath } from 'd3-geo'
import { feature } from 'topojson-client'
import type { FeatureCollection, Geometry } from 'geojson'
import type { Topology } from 'topojson-specification'
import topology from 'world-atlas/countries-110m.json'
import { numericToAlpha2 } from 'i18n-iso-countries'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { formatPercent } from './formatters'
import { slaRateTier, type SlaTier } from './slaHealth'
import type { ResponseTimeByCountryPanel } from '@/types/analytics'

const WIDTH = 980
const HEIGHT = 420

const TIER_FILL: Record<SlaTier, string> = {
  good: '#10b981',
  warn: '#f59e0b',
  bad: '#f43f5e',
  none: 'transparent',
}

interface ResponseTimeWorldMapProps {
  data: ResponseTimeByCountryPanel
  mode: 'absolute' | 'relative'
}

type CountryFeature = GeoJSON.Feature<Geometry, { name: string }>

const COUNTRIES_FC = feature(
  topology as unknown as Topology,
  (topology as unknown as Topology).objects.countries,
) as FeatureCollection<Geometry, { name: string }>

const PROJECTION = geoNaturalEarth1().fitSize([WIDTH, HEIGHT], COUNTRIES_FC)
const PATH = geoPath(PROJECTION)

export function ResponseTimeWorldMap({ data, mode }: ResponseTimeWorldMapProps) {
  const [hovered, setHovered] = useState<{ code: string; name: string; pct: number; replies: number } | null>(null)

  const byCode = useMemo(() => {
    const m = new Map<string, { pct: number; replies: number; tier: SlaTier }>()
    for (const row of data.rows) {
      const pct = mode === 'relative' ? row.relative_within_sla_pct : row.absolute_within_sla_pct
      if (pct == null) continue
      m.set(row.country_code.toUpperCase(), {
        pct,
        replies: row.replies,
        tier: slaRateTier(pct),
      })
    }
    return m
  }, [data.rows, mode])

  return (
    <Card className="p-4">
      <CardHeader className="px-0 pb-3 flex-row items-start justify-between space-y-0 gap-3">
        <div>
          <CardTitle className="text-base">Response time around the world</CardTitle>
          <CardDescription>
            Shaded by % of replies within SLA {mode === 'relative' ? '(business hours)' : '(wall-clock)'} · {byCode.size}{' '}
            countries
          </CardDescription>
        </div>
        <Legend />
      </CardHeader>
      <CardContent className="px-0">
        <div className="relative">
          <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full h-auto" role="img" aria-label="World response-time map">
            <g>
              {(COUNTRIES_FC.features as CountryFeature[]).map((geo) => {
                const numericId = String(geo.id ?? '')
                const alpha2 = numericToAlpha2(numericId)
                const match = alpha2 ? byCode.get(alpha2) : undefined
                const fill = match ? TIER_FILL[match.tier] : 'var(--muted)'
                return (
                  <path
                    key={numericId || geo.properties.name}
                    d={PATH(geo) ?? undefined}
                    fill={fill}
                    fillOpacity={match ? 0.85 : 0.35}
                    stroke="var(--border)"
                    strokeWidth={0.4}
                    onMouseEnter={() =>
                      match && alpha2 &&
                      setHovered({ code: alpha2, name: geo.properties.name, pct: match.pct, replies: match.replies })
                    }
                    onMouseLeave={() => setHovered(null)}
                    className={match ? 'cursor-pointer transition-opacity hover:opacity-80' : ''}
                  />
                )
              })}
            </g>
          </svg>
          {hovered && (
            <div className="pointer-events-none absolute top-2 right-2 rounded-md border border-border bg-background/95 px-3 py-2 text-xs shadow-sm">
              <div className="font-medium">{hovered.name}</div>
              <div className="tabular-nums">
                {formatPercent(hovered.pct, 0)} within SLA · {hovered.replies} replies
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function Legend() {
  const items: { tier: SlaTier; label: string }[] = [
    { tier: 'good', label: '≥ 90%' },
    { tier: 'warn', label: '60–90%' },
    { tier: 'bad', label: '< 60%' },
  ]
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      {items.map((i) => (
        <span key={i.tier} className="flex items-center gap-1">
          <span className="size-2.5 rounded-sm" style={{ background: TIER_FILL[i.tier] }} aria-hidden="true" />
          {i.label}
        </span>
      ))}
    </div>
  )
}
